import { IM } from '@cvte/message-service-sdk';
import {
  WebPlatForm,
  UidTokenIllegalCode,
  CbTypeKey
} from './const';
import { getUidAndToken } from './utils';

// Types
interface IAllRoomCbs {
  [key: string]: IRoomCbs;
}


interface IRoomCbs {
  [CbTypeKey.connectCb]: (data: { code: number; message: string }) => void;
  [CbTypeKey.recvMsgCb]: (data: unknown) => void;
  [CbTypeKey.onSingleCommandRecvCb]: (data: unknown) => void;
  [CbTypeKey.reConnectCb]: () => void;
  [CbTypeKey.disconnectCb]: () => void;
}

interface IMyIMConstructor extends IRoomCbs {
  roomName?: string;
}


interface IUserInfo {
  userId: string;
  userToken: string;
}

// Constants
const RECONNECT_INTERVAL = 2000;
const CONNECT_CALLBACK_DELAY = 500;

// Singleton instance
let imInstance: typeof IM | null = null;
let reconnectTimer: NodeJS.Timeout | null = null;
const roomCbs: IAllRoomCbs = {};

export interface RoomMessageChannel<T extends Record<string, unknown>> {
  push: (message: T) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class MyIM {
  private readonly roomName: string;

  constructor({
    roomName,
    connectCb,
    recvMsgCb,
    reConnectCb,
    disconnectCb,
    onSingleCommandRecvCb
  }: IMyIMConstructor) {
    this.roomName = roomName || 'default';
    this.initializeRoomCallbacks({
      connectCb,
      recvMsgCb,
      reConnectCb,
      disconnectCb,
      onSingleCommandRecvCb
    });
    this.initializeIM();
  }

  private initializeRoomCallbacks(callbacks: IRoomCbs): void {
    roomCbs[this.roomName] = callbacks;
  }

  private initializeIM(): void {
    if (!imInstance) {
      imInstance = new IM();
      this.startIMListen();
      this.setupReconnectTimer();
    } else {
      this.triggerExistingConnection();
    }
  }

  private setupReconnectTimer(): void {
    reconnectTimer = setInterval(() => {
      this.startIMListen();
    }, RECONNECT_INTERVAL);
  }

  private triggerExistingConnection(): void {
    setTimeout(() => {
      const callbacks = roomCbs[this.roomName];
      callbacks[CbTypeKey.connectCb]({ 
        code: 0, 
        message: '已存在im连接，主动触发连接成功cb' 
      });
    }, CONNECT_CALLBACK_DELAY);
  }

  private async initIMConfig(userInfo: IUserInfo): Promise<void> {
    const initInfo = {
      host: process.env.VITE_IM_HOST || '',
      appid: process.env.VITE_IM_APPID || '',
      uid: userInfo.userId,
      token: userInfo.userToken,
      platform: WebPlatForm,
      // auth: {
      //   header: {
      //     Authorization: `Bearer ${userInfo.userToken}`,
      //   },
      // },
    };

    if (!initInfo.host || !initInfo.appid) {
      throw new Error('Missing required IM configuration');
    }

    console.log('initIMConfig', initInfo);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    imInstance?.init(initInfo);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private executeCbs(cbType: CbTypeKey, data?: any): void {
    const callbacks = Object.values(roomCbs)
      .map(item => item[cbType])
      .filter(Boolean);

    callbacks.forEach(callback => {
      try {
        if (cbType === CbTypeKey.connectCb) {
          const connectData = {
            code: data?.code ?? 0,
            message: data?.message ?? ''
          };
          callback(connectData);
        } else {
          callback(data);
        }
      } catch (error) {
        console.error(`Error executing ${cbType} callback:`, error);
      }
    });
  }

  public async startIMListen(forceUpdateUserInfo = false): Promise<void> {
    try {
      const userInfo = await getUidAndToken(forceUpdateUserInfo);
      if (!userInfo) {
        throw new Error('Failed to get user info');
      }

      if (reconnectTimer) {
        clearInterval(reconnectTimer);
      }

      await this.initIMConfig(userInfo);
      this.setupIMListeners();
    } catch (error) {
      console.error('Failed to start IM listener:', error);
      this.executeCbs(CbTypeKey.disconnectCb);
    }
  }

  private setupIMListeners(): void {
    if (!imInstance) return;

    imInstance
      .login()
      .onConnect((data: { code: number; message: string }) => {
        console.log('connect im success', data);
        this.executeCbs(CbTypeKey.connectCb, data);
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .onDisconnect((error: any) => {
        console.log('im disconnect', error);
        if (error.code === UidTokenIllegalCode) {
          this.startIMListen(true);
        } else {
          this.executeCbs(CbTypeKey.disconnectCb);
          imInstance = null;
        }
      })
      .onReconnect(() => {
        console.log('im onReconnect');
        this.executeCbs(CbTypeKey.reConnectCb);
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .onError((err: any) => {
        console.error('im error', err);
      })
      .onMessageRecv(async (data: { msg_body: { text?: string } }) => {
        console.log('im onMessageRecv', data);
        if (!data.msg_body.text) return;

        try {
          const message = JSON.parse(decodeURIComponent(data.msg_body.text));
          this.executeCbs(CbTypeKey.recvMsgCb, message);
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      })
      .onSingleCommandRecv((data: unknown) => {
        console.log('im onSingleCommandRecv', data);
        this.executeCbs(CbTypeKey.onSingleCommandRecvCb, data);
      })

  }

  public logout() {
    if (imInstance) {
      imInstance.logout();
    }
  }
}